(ns frontend.test.date-picker.view
  (:require
   [basic-components.modal :as zap-modal]
   [frontend.view.modals.events :as modals]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.date-picker.view :as date-picker]
   [common.date-formats :as date-formats]
   [oops.core :as oops]
   [clojure.string :as string]
   [common.util :as cu]
   [ui.dom-globals :as dg]))

;; For testing only
(defn format-date [millis time-zone]
  (let [date (js/Date. millis)
        options #js {:weekday "long"
                     :year "numeric"
                     :month "long"
                     :day "numeric"
                     :hour "2-digit"
                     :minute "2-digit"
                     :second "2-digit"
                     :timeZone time-zone
                     :timeZoneName "short"}
        formatter (js/Intl.DateTimeFormat. "en-GB" options)]
    (.format formatter date)))


(def basic-state-variations
  [{:title "Default"}
   {:title     "Disabled"
    :disabled? true}
   {:title  "Error"
    :error? true}
   {:title "Not clearable"
    :not-clearable? true}])

(def variant-variations
  [{:title "Default (outline)"}
   {:title   "Underline"
    :variant :underline}
   {:title           "Outline transparent"
    :variant         :outline
    :transparent? true}
   {:title           "Underline transparent"
    :variant         :underline
    :transparent? true}])

(def date-format-variations
  (->> date-formats/options
       (map (fn [{:keys [key]}]
              {:title       key
               :date-format key}))
       (concat [{:title "Default"}])))


(defn test-section
  [state section-title prop-variants]
  [:div
   [:h2.title-h2.mt-4.mb-2 section-title]
   [:div.d-flex
    {:style {:gap       "20px"
             :flex-wrap "wrap"}}
    (->> prop-variants
         (map-indexed (fn [index props]
                        (let [title (:title props)
                              props (dissoc props :title)]
                          ^{:key (str section-title "-" index)}
                          [:div
                           [:p.mb-1 title]
                           [date-picker/view
                            (merge
                             {:value     (:value @state)
                              :on-change (fn [new-value]
                                           (swap! state assoc :value new-value))}
                             props)]])))
         (doall))]])

(defn view
  []
  (let [state (r/atom {:value 1694638800000
                       :single-value 1746230400000
                       :date-format "dd/MM/yyyy"
                       :error-text nil})
        min-date (js/Date.)
        _ (.setFullYear min-date 2015)]
    (fn []
      [:div.d-flex.flex-column.align-items-start
       {:style {:gap "50px"
                :margin-bottom "200px"}}
       [:div
        {:class "p-4 flex flex-col gap-4"}
        [:h2
         {:class "text-xl text-bold"}
         "New Datepicker"]
        [:p "Datepicker is accessible via keyboard"]

        [:div
         [:p "Date format"]
         [:select
          {:value (:date-format @state)
           :on-change (fn [e]
                        (let [value (dg/event-target-value e)]
                          (swap! state assoc :date-format value)))}
          (->> date-formats/formats
               (map (fn [format]
                      ^{:key format}
                      [:option
                       {:key format
                        :value format}
                       format])))]]

        [:div
         [date-picker/view
          {:value       (:single-value @state)
           :on-change   (fn [new-value]
                          (swap! state assoc :single-value new-value))
           :full-width? true
           :date-format (:date-format @state)
           :on-error! (fn [error-text]
                        (swap! state assoc :error-text error-text))
           :clear-error! #(swap! state dissoc :error-text)}]
         (when-let [error-text (:error-text @state)]
           [:p
            {:class "text-sm m-0 text-danger-regular"}
            error-text])]

        (when-let [millis (:single-value @state)]
          (let [gmt (format-date millis "GMT")
                time-zone (-> js/Intl
                              (oops/ocall "DateTimeFormat")
                              (oops/ocall "resolvedOptions")
                              (oops/oget "timeZone"))
                local (format-date millis time-zone)]
            [:div
             [:div [:strong "Time millis: "] millis]
             [:div [:strong "GMT Time: "] gmt]
             [:div [:strong (str "Local Time " time-zone ": ")] local]]))


        [:div
         {:class "mt-10"}
         [:input
          {:type :date
           :on-change (fn [e]
                        (let [value-str (-> e .-target .-value)]
                          (when-not (cu/blank-value? value-str)

                            (let [parts (string/split value-str #"-")
                                  [year month day] (->> parts
                                                        (mapv js/parseInt))
                                  utc-millis (js/Date.UTC year (dec month) day 0 0 0 0)]
                              (js/console.log "utc millis " utc-millis)
                              (swap! state assoc :single-value utc-millis)))))}]]


        (comment
          [test-section state "Basic states" basic-state-variations]

          [:div
           [:h2.title-h2.mt-4.mb-2 "Resize"]

           [:div
            {:style {:display        "flex"
                     :flex-direction "column"
                     :gap            "20px"
                     :border         "2px solid black"
                     :padding        "20px"
                     :width          "400px"
                     :resize         "horizontal"
                     :overflow       "auto"}}
            [date-picker/view
             {:value       (:value @state)
              :on-change   (fn [new-value]
                             (swap! state assoc :value new-value))
              :full-width? true}]

            [date-picker/view
             {:value           (:value @state)
              :on-change       (fn [new-value]
                                 (swap! state assoc :value new-value))
              :full-width?     true
              :transparent? true
              :variant         :underline
              :date-format     "dd.MM.yyyy"}]]]

          [test-section state "Variants" variant-variations]
          [test-section state "Date formats" date-format-variations]

          [:div
           [:h2.title-h2.mt-4.mb-2 "Zap Modal"]
           [button/primary-solid
            {:on-click (fn []
                         (modals/open-modal! zap-modal/modal-key
                                             {:title   "Full screen modal"
                                              :content (fn []
                                                         [:div
                                                          (js/console.log "Modal date render")
                                                          [:p.mb-1 "Date"]
                                                          [date-picker/view
                                                           {:value     (:value @state)
                                                            :on-change (fn [new-value]
                                                                         (js/console.log "Modal date value " new-value)
                                                                         (swap! state assoc :value new-value))}]])}))}
            "Open "]])]])))
